import React, { useEffect, useState } from 'react';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, notification, Row, Space, Spin } from 'antd';
import { useIntl } from 'react-intl';
import {
    useGetPartnerLegalVatCodeLazyQuery,
} from '../obtQueries.generated';
import {
    useUpdatePartnerLegalVatCodeMutation,
    useDeletePartnerLegalVatCodeMutation,
} from '../obtMutations.generated';

// ─── Constants ─────────────────────────────────────────────────────────────────

const POLAND_VAT_CODE_LENGTH = 10;

// ─── Types ─────────────────────────────────────────────────────────────────────

type ObtPolandVatFormProps = {
    partnerno: string;
    idpartner: number;
    canUpdate: boolean;
};

// ─── Component ─────────────────────────────────────────────────────────────────

const ObtPolandVatForm: React.FC<ObtPolandVatFormProps> = ({
    partnerno,
    idpartner,
    canUpdate,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [currentVatCode, setCurrentVatCode] = useState<string | undefined>(undefined);

    const [getVatCode] = useGetPartnerLegalVatCodeLazyQuery();
    const [updateVatCode] = useUpdatePartnerLegalVatCodeMutation();
    const [deleteVatCode] = useDeletePartnerLegalVatCodeMutation();

    // Load VAT code on mount
    useEffect(() => {
        if (partnerno && idpartner) {
            setLoading(true);
            getVatCode({ variables: { partnerno, idpartner }, fetchPolicy: 'network-only' })
                .then((response) => {
                    const vatNipInfo = response.data?.obtGetPartnerLegalVatNipInfo;
                    const code = vatNipInfo?.vatcode ?? undefined;
                    setCurrentVatCode(code);
                    form.setFieldsValue({ vatCode: code });
                })
                .catch((err) => {
                    console.error('Error loading VAT(NIP):', err);
                    notification.error({
                        message: intl.formatMessage({ id: 'obt.legal.poland.load.error', defaultMessage: 'Error loading VAT(NIP) information.' }),
                    });
                })
                .finally(() => setLoading(false));
        } else {
            form.resetFields();
        }
    }, [partnerno, idpartner, getVatCode, form, intl]);

    const validatePolandVatCode = (_rule: unknown, value: string): Promise<void> => {
        if (value && value.length !== POLAND_VAT_CODE_LENGTH) {
            return Promise.reject(
                intl.formatMessage(
                    { id: 'obt.legal.poland.vatCode.lengthError', defaultMessage: 'VAT(NIP) must be exactly {length} characters.' },
                    { length: POLAND_VAT_CODE_LENGTH }
                )
            );
        }
        return Promise.resolve();
    };

    const handleSave = async (values: { vatCode: string }) => {
        setLoading(true);
        try {
            const response = await updateVatCode({
                variables: { idpartner, partnerno, vatCode: values.vatCode },
            });
            const result = response.data?.obtUpdatePartnerLegalVatCode;
            if (result?.success === false) {
                notification.error({
                    message: intl.formatMessage({ id: 'obt.legal.poland.save.error', defaultMessage: 'Error updating VAT(NIP).' }),
                    description: result.message ?? undefined,
                });
            } else {
                setCurrentVatCode(values.vatCode);
                notification.success({
                    message: intl.formatMessage({ id: 'obt.legal.poland.save.success', defaultMessage: 'VAT(NIP) updated successfully.' }),
                });
            }
        } catch (err) {
            console.error('Error saving VAT(NIP):', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.poland.save.error', defaultMessage: 'Error updating VAT(NIP).' }),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!currentVatCode) return;

        setLoading(true);
        try {
            const response = await deleteVatCode({
                variables: { idpartner, partnerno, vatCode: currentVatCode },
            });
            const result = response.data?.obtDeletePartnerLegalVatCode;
            if (result?.success) {
                form.resetFields();
                setCurrentVatCode(undefined);
                notification.success({
                    message: intl.formatMessage({ id: 'obt.legal.poland.delete.success', defaultMessage: 'VAT(NIP) removed successfully.' }),
                });
            } else {
                notification.error({
                    message: intl.formatMessage({ id: 'obt.legal.poland.delete.error', defaultMessage: 'Error deleting VAT(NIP).' }),
                    description: result?.message ?? undefined,
                });
            }
        } catch (err) {
            console.error('Error deleting VAT(NIP):', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.poland.delete.error', defaultMessage: 'Error deleting VAT(NIP).' }),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Spin spinning={loading} size="large">
            <Form layout="vertical" onFinish={handleSave} form={form}>
                <Card
                    title={intl.formatMessage({ id: 'obt.legal.poland.title', defaultMessage: 'Poland VAT (NIP) Legal Details' })}
                    size="small"
                    
                >
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="vatCode"
                                label={intl.formatMessage({ id: 'obt.legal.poland.vatCode', defaultMessage: 'VAT (NIP)' })}
                                rules={[
                                    { required: true },
                                    { validator: validatePolandVatCode },
                                ]}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.poland.vatCode', defaultMessage: 'VAT (NIP)' })}
                                    disabled={!canUpdate}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col>
                            <Space>
                                <Button
                                    icon={<SaveOutlined />}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={!canUpdate}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.save', defaultMessage: 'Save' })}
                                </Button>
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    disabled={!canUpdate || !currentVatCode}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.delete', defaultMessage: 'Delete' })}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Spin>
    );
};

export default ObtPolandVatForm;

